import { useRef, useState, useCallback } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../i18n";
import { useSEO } from "../seo";
import { FadeIn, MaskLines, ParallaxImage, Marquee } from "../components/Reveal";
import { CTAButton, SectionLabel } from "../components/Bits";

const IMG = {
  hero: "https://images.unsplash.com/photo-1487958449943-2429e8be8625?q=80&w=2000&auto=format&fit=crop",
  acquistiamo: "https://images.pexels.com/photos/36570074/pexels-photo-36570074.jpeg?auto=compress&cs=tinysrgb&w=1600",
  trasformiamo: "https://images.unsplash.com/photo-1431576901776-e539bd916ba2?q=80&w=1600&auto=format&fit=crop",
  valorizziamo: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=1600&auto=format&fit=crop",
  dark: "https://images.unsplash.com/photo-1565964450734-c6b267945d7a?q=80&w=2000&auto=format&fit=crop",
  consulenza: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=1600&auto=format&fit=crop",
  cambiUso: "https://images.unsplash.com/photo-1493397212122-2b85dda8106b?q=80&w=1600&auto=format&fit=crop",
  ristrutturazioni: "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?q=80&w=1600&auto=format&fit=crop",
  gc: "https://images.unsplash.com/photo-1479839672679-a46483c0e7c8?q=80&w=1600&auto=format&fit=crop",
  p1b: "/images/prima-milano-appartamento.webp",
  p1a: "https://images.pexels.com/photos/7546314/pexels-photo-7546314.jpeg?auto=compress&cs=tinysrgb&w=1400",
  p2b: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=1400&auto=format&fit=crop",
  p2a: "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?q=80&w=1400&auto=format&fit=crop",
  p3b: "https://images.unsplash.com/photo-1481277542470-605612bd2d61?q=80&w=1400&auto=format&fit=crop",
  p3a: "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?q=80&w=1400&auto=format&fit=crop",
};

const copy = {
  it: {
    seoTitle: "Nicole Srl | Investimenti e Valorizzazione Immobiliare",
    seoDesc: "Nicole Srl acquista, trasforma e valorizza immobili. Operazioni immobiliari, riqualificazione e acquisizioni anche in situazioni complesse e saldo e stralcio.",
    heroKicker: "Real Estate Investments",
    heroLines: ["Acquistiamo immobili.", "Li trasformiamo.", "Creiamo valore."],
    heroSub:
      "Nicole Srl è una società immobiliare specializzata nell'acquisizione, trasformazione e valorizzazione di immobili, incluse operazioni caratterizzate da situazioni debitorie o procedure complesse.",
    ctaScopri: "Scopri Nicole Srl",
    scrollHint: "Scorri",
    introLabel: "La società",
    introTitle: "Real estate, con una visione imprenditoriale.",
    introP1:
      "Non ci limitiamo a comprare immobili. Ne analizziamo il potenziale, studiamo il miglior posizionamento, interveniamo sulla distribuzione degli spazi, sul design e sulla qualità dell'immobile per riportarlo sul mercato con un'identità completamente nuova.",
    introP2:
      "Dall'acquisizione alla ristrutturazione fino alla rivendita, gestiamo direttamente l'intero processo di valorizzazione.",
    marquee: ["Acquistiamo", "Analizziamo", "Trasformiamo", "Valorizziamo"],
    manifestoLabel: "Il metodo",
    steps: [
      {
        n: "01",
        title: "Acquistiamo",
        text: "Valutiamo opportunità immobiliari a Milano e nelle principali aree della Lombardia. Analizziamo posizione, caratteristiche, criticità e potenziale dell'immobile con un approccio rapido e imprenditoriale.",
        cta: "Proponici il tuo immobile",
        img: IMG.acquistiamo,
        alt: "Facciata storica elegante di un edificio a Milano",
      },
      {
        n: "02",
        title: "Trasformiamo",
        text: "Ogni progetto nasce dallo studio del potenziale dell'immobile. Layout, materiali, impianti, illuminazione, finiture e interior design vengono studiati per aumentare qualità percepita, funzionalità e valore commerciale. Non ristrutturiamo semplicemente. Riposizioniamo immobili.",
        img: IMG.trasformiamo,
        alt: "Cantiere elegante di riqualificazione architettonica",
      },
      {
        n: "03",
        title: "Valorizziamo",
        text: "Il progetto viene sviluppato pensando fin dall'inizio al mercato finale. Architettura, estetica, funzionalità e posizionamento commerciale lavorano insieme per trasformare un immobile ordinario in un prodotto immobiliare desiderabile.",
        img: IMG.valorizziamo,
        alt: "Interno contemporaneo ristrutturato con materiali di qualità",
      },
    ],
    baLabel: "Trasformazioni",
    baTitle: "Prima. Dopo. Il valore della trasformazione.",
    baLabels: { loc: "Località", tip: "Tipologia", int: "Intervento", before: "Prima", after: "Dopo" },
    projects: [
      { imgBefore: IMG.p1b, imgAfter: IMG.p1a, loc: "Milano", tip: "Appartamento residenziale", int: "Riqualificazione completa" },
      { imgBefore: IMG.p2b, imgAfter: IMG.p2a, loc: "Como", tip: "Attico", int: "Ristrutturazione e interior design" },
      { imgBefore: IMG.p3b, imgAfter: IMG.p3a, loc: "Bergamo", tip: "Immobile misto", int: "Riposizionamento" },
    ],
    darkLabel: "Operazioni complesse",
    darkTitle: "Dove altri vedono un problema, noi analizziamo un'operazione.",
    darkP1:
      "Alcuni immobili non possono essere acquistati o venduti attraverso una compravendita ordinaria. Esposizioni debitorie, ipoteche, procedure esecutive, più creditori o altre criticità possono rendere necessario un percorso specifico.",
    darkP2:
      "Nicole Srl valuta direttamente operazioni immobiliari complesse e, quando esistono le condizioni, struttura un percorso finalizzato alla definizione della situazione e alla compravendita dell'immobile.",
    consLabel: "Consulenza immobiliare",
    consTitle: "Ogni immobile può avere più di una possibilità.",
    consP1:
      "Il valore di un immobile non dipende solamente dalle sue condizioni attuali. Distribuzione interna, destinazione urbanistica, possibilità di frazionamento, target finale e livello della riqualificazione possono modificare profondamente il risultato di un'operazione.",
    consP2:
      "Nicole Srl affianca proprietari e investitori nell'analisi strategica dell'immobile, individuando possibili percorsi di valorizzazione prima di affrontare l'investimento.",
    consCta: "Richiedi una consulenza",
    frazLabel: "Frazionamenti immobiliari",
    frazTitle: "Un immobile. Più opportunità.",
    frazP1:
      "Il frazionamento può trasformare completamente l'economia di un'operazione immobiliare. Studiamo il potenziale dell'immobile valutando distribuzione degli spazi, accessi, impianti, requisiti tecnici, mercato di riferimento e possibile configurazione finale.",
    frazP2:
      "Quando l'operazione risulta interessante, il progetto viene sviluppato insieme ai tecnici incaricati per verificarne concretamente la fattibilità urbanistica, edilizia e catastale.",
    frazSteps: ["Immobile esistente", "Analisi", "Frazionamento", "Riqualificazione", "Nuovi immobili", "Valorizzazione"],
    frazPlanA: "Una unità",
    frazPlanB: "Più unità",
    usoLabel: "Cambi di destinazione d'uso",
    usoTitle: "Cambiare funzione. Creare valore.",
    usoP1:
      "Alcuni immobili possiedono un potenziale che la destinazione attuale non riesce ad esprimere. Nicole Srl analizza opportunità di trasformazione attraverso il cambio di destinazione d'uso, valutando preliminarmente il possibile nuovo utilizzo dell'immobile e la sostenibilità economica dell'operazione.",
    usoRows: [
      ["Ufficio", "Residenziale"],
      ["Negozio", "Residenziale"],
      ["Laboratorio", "Residenziale"],
      ["Immobile accessorio", "Nuova funzione"],
    ],
    usoNote:
      "Ogni cambio di destinazione d'uso è subordinato alla verifica della normativa urbanistica, edilizia, condominiale e tecnica applicabile al singolo immobile.",
    ristLabel: "Ristrutturazioni",
    ristTitle: "Trasformiamo immobili. Anche per conto dei nostri clienti.",
    ristText:
      "L'esperienza maturata nelle nostre operazioni immobiliari viene applicata anche agli interventi commissionati da proprietari e investitori. Dalla definizione della strategia alla gestione delle lavorazioni, coordiniamo il progetto con un obiettivo preciso: ottenere un immobile più funzionale, contemporaneo e valorizzato.",
    ristCta: "Parlaci del tuo progetto",
    gcLabel: "General Contractor",
    gcTitle: "Un unico interlocutore. Dall'idea alla consegna.",
    gcText:
      "Gestire una ristrutturazione significa coordinare imprese, fornitori, tecnici, lavorazioni, materiali e tempi. Come General Contractor, Nicole Srl coordina il processo e le figure coinvolte, offrendo al cliente un unico riferimento durante l'intervento.",
    gcCta: "Richiedi una valutazione del progetto",
    finalTitle: "Hai un immobile da proporre?",
    finalText: "Raccontaci l'immobile o l'operazione. Valutiamo ogni segnalazione con riservatezza e rapidità.",
  },
  en: {
    seoTitle: "Nicole Srl | Real Estate Investment & Value Creation",
    seoDesc: "Nicole Srl acquires, transforms and enhances properties. Real estate operations, redevelopment and acquisitions including complex situations and distressed settlements.",
    heroKicker: "Real Estate Investments",
    heroLines: ["We acquire properties.", "We transform them.", "We create value."],
    heroSub:
      "Nicole Srl is a real estate company specialised in the acquisition, transformation and enhancement of properties, including operations involving debt exposure or complex procedures.",
    ctaScopri: "Discover Nicole Srl",
    scrollHint: "Scroll",
    introLabel: "The company",
    introTitle: "Real estate, with an entrepreneurial vision.",
    introP1:
      "We don't simply buy properties. We analyse their potential, study the best positioning, and intervene on layout, design and quality to bring them back to the market with a completely new identity.",
    introP2:
      "From acquisition to renovation to resale, we directly manage the entire value-creation process.",
    marquee: ["Acquire", "Analyse", "Transform", "Enhance"],
    manifestoLabel: "The method",
    steps: [
      {
        n: "01",
        title: "We acquire",
        text: "We assess real estate opportunities in Milan and the main areas of Lombardy. We analyse location, features, criticalities and potential with a fast, entrepreneurial approach.",
        cta: "Propose your property",
        img: IMG.acquistiamo,
        alt: "Elegant historic building facade in Milan",
      },
      {
        n: "02",
        title: "We transform",
        text: "Every project starts from studying the property's potential. Layout, materials, systems, lighting, finishes and interior design are studied to increase perceived quality, functionality and commercial value. We don't simply renovate. We reposition properties.",
        img: IMG.trasformiamo,
        alt: "Elegant architectural redevelopment site",
      },
      {
        n: "03",
        title: "We enhance",
        text: "Every project is developed with the final market in mind from day one. Architecture, aesthetics, functionality and commercial positioning work together to turn an ordinary property into a desirable real estate product.",
        img: IMG.valorizziamo,
        alt: "Renovated contemporary interior with quality materials",
      },
    ],
    baLabel: "Transformations",
    baTitle: "Before. After. The value of transformation.",
    baLabels: { loc: "Location", tip: "Type", int: "Intervention", before: "Before", after: "After" },
    projects: [
      { imgBefore: IMG.p1b, imgAfter: IMG.p1a, loc: "Milan", tip: "Residential apartment", int: "Full redevelopment" },
      { imgBefore: IMG.p2b, imgAfter: IMG.p2a, loc: "Como", tip: "Penthouse", int: "Renovation & interior design" },
      { imgBefore: IMG.p3b, imgAfter: IMG.p3a, loc: "Bergamo", tip: "Mixed-use property", int: "Repositioning" },
    ],
    darkLabel: "Complex operations",
    darkTitle: "Where others see a problem, we analyse an operation.",
    darkP1:
      "Some properties cannot be bought or sold through an ordinary transaction. Debt exposure, mortgages, enforcement procedures, multiple creditors or other criticalities may require a specific path.",
    darkP2:
      "Nicole Srl directly assesses complex real estate operations and, where conditions allow, structures a path aimed at resolving the situation and completing the sale.",
    consLabel: "Real estate advisory",
    consTitle: "A property can have more than one possibility.",
    consP1:
      "The value of a property does not depend only on its current condition. Internal layout, planning designation, subdivision potential, final target and redevelopment level can profoundly change the outcome of an operation.",
    consP2:
      "Nicole Srl supports owners and investors in the strategic analysis of the property, identifying possible value-creation paths before committing to the investment.",
    consCta: "Request advisory",
    frazLabel: "Property subdivision",
    frazTitle: "One property. More opportunities.",
    frazP1:
      "Subdivision can completely transform the economics of a real estate operation. We study the property's potential by assessing layout, access, systems, technical requirements, reference market and possible final configuration.",
    frazP2:
      "When the operation proves attractive, the project is developed together with appointed technicians to concretely verify its planning, building and cadastral feasibility.",
    frazSteps: ["Existing property", "Analysis", "Subdivision", "Redevelopment", "New units", "Value creation"],
    frazPlanA: "One unit",
    frazPlanB: "Multiple units",
    usoLabel: "Change of use",
    usoTitle: "Change function. Create value.",
    usoP1:
      "Some properties hold a potential their current designation cannot express. Nicole Srl analyses transformation opportunities through change of use, preliminarily assessing the property's possible new function and the economic sustainability of the operation.",
    usoRows: [
      ["Office", "Residential"],
      ["Retail unit", "Residential"],
      ["Workshop", "Residential"],
      ["Ancillary property", "New function"],
    ],
    usoNote:
      "Every change of use is subject to verification of the planning, building, condominium and technical regulations applicable to the individual property.",
    ristLabel: "Renovations",
    ristTitle: "We transform properties. Also on behalf of our clients.",
    ristText:
      "The experience gained in our own real estate operations is applied to interventions commissioned by owners and investors. From defining the strategy to managing the works, we coordinate the project with one precise goal: a more functional, contemporary and valuable property.",
    ristCta: "Tell us about your project",
    gcLabel: "General Contractor",
    gcTitle: "One point of contact. From idea to delivery.",
    gcText:
      "Managing a renovation means coordinating contractors, suppliers, technicians, works, materials and timelines. As General Contractor, Nicole Srl coordinates the process and the parties involved, offering the client a single point of reference throughout the intervention.",
    gcCta: "Request a project assessment",
    finalTitle: "Have a property to propose?",
    finalText: "Tell us about the property or the operation. We assess every submission with confidentiality and speed.",
  },
};

const BeforeAfter = ({ project, labels, index }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);

  const update = useCallback((clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, pct)));
  }, []);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    update(e.clientX);
  };

  return (
    <FadeIn delay={index * 0.08}>
      <div
        ref={ref}
        data-testid={`ba-slider-${index}`}
        className="ba-handle relative aspect-[4/3] w-full cursor-ew-resize select-none overflow-hidden bg-dove"
        onPointerDown={onPointerDown}
        onPointerMove={(e) => e.buttons === 1 && update(e.clientX)}
      >
        <img src={project.imgAfter} alt={`${labels.after} — ${project.tip}`} className="absolute inset-0 h-full w-full object-cover" draggable="false" loading="lazy" decoding="async" />
        <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
          <img src={project.imgBefore} alt={`${labels.before} — ${project.tip}`} className="absolute inset-0 h-full w-full object-cover grayscale-[35%]" draggable="false" loading="lazy" decoding="async" />
        </div>
        <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
          <div className="absolute inset-y-0 -left-px w-px bg-ivory" />
          <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full border border-ivory/60 bg-ink/40 backdrop-blur-sm">
            <span className="text-[10px] tracking-[0.1em] text-ivory">‹ ›</span>
          </div>
        </div>
        <span className="absolute left-4 top-4 bg-ink/50 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-ivory backdrop-blur-sm">{labels.before}</span>
        <span className="absolute right-4 top-4 bg-ivory/70 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-ink backdrop-blur-sm">{labels.after}</span>
      </div>
      <dl className="mt-5 grid grid-cols-3 gap-4 border-t border-ink/10 pt-5">
        {[
          [labels.loc, project.loc],
          [labels.tip, project.tip],
          [labels.int, project.int],
        ].map(([dt, dd]) => (
          <div key={dt}>
            <dt className="text-[10px] uppercase tracking-[0.22em] text-stone">{dt}</dt>
            <dd className="mt-1.5 text-sm leading-snug">{dd}</dd>
          </div>
        ))}
      </dl>
    </FadeIn>
  );
};

const FloorPlanMulti = () => (
  <div className="flex h-full w-full gap-1.5">
    <div className="flex flex-1 flex-col gap-1.5">
      <div className="flex-[2] border border-ink/50" />
      <div className="flex flex-1 gap-1.5">
        <div className="flex-1 border border-ink/50" />
        <div className="flex-1 border border-ink/50" />
      </div>
    </div>
    <div className="flex flex-1 flex-col gap-1.5">
      <div className="flex flex-1 gap-1.5">
        <div className="flex-1 border border-ink/50" />
        <div className="flex-[1.4] border border-ink/50" />
      </div>
      <div className="flex-[2] border border-ink/50" />
    </div>
  </div>
);

const FloorPlanSingle = () => (
  <div className="flex h-full w-full flex-col gap-1.5">
    <div className="flex-[2] border border-ink/50" />
    <div className="flex flex-1 gap-1.5">
      <div className="flex-1 border border-ink/50" />
      <div className="flex-1 border border-ink/50" />
    </div>
  </div>
);

export default function Home() {
  const { lang } = useLang();
  const t = copy[lang];
  useSEO({ title: t.seoTitle, description: t.seoDesc, path: "/", lang });

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0.15]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-[100svh] items-end overflow-hidden bg-ink">
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <motion.img
            src={IMG.hero}
            alt={lang === "it" ? "Architettura contemporanea — facciata bianca minimalista" : "Contemporary architecture — minimalist white facade"}
            className="h-full w-full object-cover"
            initial={{ scale: 1.12 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1] }}
            fetchpriority="high"
          />
        </motion.div>
        <div className="hero-vignette absolute inset-0" />
        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 mx-auto w-full max-w-[1600px] px-6 pb-20 md:px-10 md:pb-28">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="mb-6 text-[11px] font-medium uppercase tracking-[0.3em] text-ivory/70"
          >
            {t.heroKicker}
          </motion.p>
          <h1 className="max-w-5xl font-serif text-[11.5vw] font-light leading-[1.04] tracking-tight text-ivory sm:text-6xl md:text-7xl lg:text-[6rem]">
            <MaskLines lines={t.heroLines} delay={0.35} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-xl text-sm leading-relaxed text-ivory/80 md:text-base"
          >
            {t.heroSub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <CTAButton to="/contatti" variant="light" testId="hero-cta-proponi">
              {lang === "it" ? "Proponici un immobile" : "Propose a property"}
            </CTAButton>
            <CTAButton to="/chi-siamo" variant="outlineLight" testId="hero-cta-scopri">
              {t.ctaScopri}
            </CTAButton>
          </motion.div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 right-6 z-10 hidden items-center gap-3 md:right-10 md:flex"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] text-ivory/50">{t.scrollHint}</span>
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            className="block h-10 w-px bg-ivory/40"
          />
        </motion.div>
      </section>

      {/* MARQUEE */}
      <Marquee items={t.marquee} />

      {/* INTRO */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-40">
        <FadeIn>
          <SectionLabel>{t.introLabel}</SectionLabel>
        </FadeIn>
        <div className="mt-10 grid gap-12 md:mt-16 md:grid-cols-12">
          <FadeIn className="md:col-span-7" delay={0.1}>
            <h2 className="font-serif text-4xl font-light leading-[1.08] tracking-tight md:text-6xl">
              {t.introTitle}
            </h2>
          </FadeIn>
          <FadeIn className="md:col-span-4 md:col-start-9" delay={0.2}>
            <p className="text-base leading-relaxed text-stone">{t.introP1}</p>
            <p className="mt-6 border-l border-bronze pl-6 font-serif text-lg font-light italic leading-relaxed text-ink">
              {t.introP2}
            </p>
          </FadeIn>
        </div>
      </section>

      {/* MANIFESTO 01 / 02 / 03 */}
      <section className="border-t border-ink/10">
        <div className="mx-auto max-w-7xl px-6 pt-24 md:px-10 md:pt-32">
          <FadeIn>
            <SectionLabel>{t.manifestoLabel}</SectionLabel>
          </FadeIn>
        </div>
        {t.steps.map((step, i) => (
          <div key={step.n} className="mx-auto grid max-w-7xl gap-10 px-6 py-20 md:grid-cols-12 md:items-center md:gap-16 md:px-10 md:py-28">
            <div className={`md:col-span-5 ${i % 2 === 1 ? "md:order-2 md:col-start-8" : ""}`}>
              <FadeIn>
                <div className="font-serif text-[5.5rem] font-light leading-none text-ink/10 md:text-[8rem]">{step.n}</div>
                <h3 className="-mt-8 font-serif text-4xl font-light tracking-tight md:-mt-14 md:text-6xl">{step.title}</h3>
                <p className="mt-7 max-w-md text-base leading-relaxed text-stone">{step.text}</p>
                {step.cta && (
                  <div className="mt-9">
                    <CTAButton to="/contatti" variant="outline" testId={`manifesto-cta-${step.n}`}>
                      {step.cta}
                    </CTAButton>
                  </div>
                )}
              </FadeIn>
            </div>
            <div className={`md:col-span-7 ${i % 2 === 1 ? "md:order-1 md:col-start-1" : ""}`}>
              <FadeIn delay={0.12}>
                <ParallaxImage src={step.img} alt={step.alt} className="aspect-[4/3] w-full md:aspect-[16/10]" />
              </FadeIn>
            </div>
          </div>
        ))}
      </section>

      {/* PRIMA / DOPO */}
      <section className="border-t border-ink/10 bg-dove/40">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
          <FadeIn>
            <SectionLabel>{t.baLabel}</SectionLabel>
            <h2 className="mt-10 max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight md:mt-14 md:text-6xl">
              {t.baTitle}
            </h2>
          </FadeIn>
          <div className="mt-16 grid gap-14 md:mt-24 md:grid-cols-3 md:gap-8">
            {t.projects.map((p, i) => (
              <BeforeAfter key={i} project={p} labels={t.baLabels} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* OPERAZIONI COMPLESSE — dark */}
      <section className="relative overflow-hidden bg-ink text-ivory">
        <ParallaxImage src={IMG.dark} alt="" className="absolute inset-0 opacity-25" speed={0.05} />
        <div className="relative mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-44">
          <FadeIn>
            <SectionLabel dark>{t.darkLabel}</SectionLabel>
          </FadeIn>
          <FadeIn delay={0.1}>
            <h2 className="mt-10 max-w-4xl font-serif text-4xl font-light leading-[1.1] tracking-tight md:mt-14 md:text-6xl">
              {t.darkTitle}
            </h2>
          </FadeIn>
          <div className="mt-12 grid gap-10 md:grid-cols-2 md:gap-16">
            <FadeIn delay={0.18}>
              <p className="max-w-lg text-base leading-relaxed text-ivory/70">{t.darkP1}</p>
            </FadeIn>
            <FadeIn delay={0.26}>
              <p className="max-w-lg text-base leading-relaxed text-ivory/70">{t.darkP2}</p>
              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <CTAButton to="/contatti" variant="light" testId="dark-cta-situazione">
                  {lang === "it" ? "Parlaci della situazione" : "Tell us about the situation"}
                </CTAButton>
                <CTAButton to="/saldo-e-stralcio" variant="outlineLight" testId="dark-cta-saldo">
                  {lang === "it" ? "Saldo e stralcio" : "Distressed settlements"}
                </CTAButton>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* CONSULENZA */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-40">
        <div className="grid gap-12 md:grid-cols-12 md:items-end">
          <div className="md:col-span-7">
            <FadeIn>
              <SectionLabel>{t.consLabel}</SectionLabel>
              <h2 className="mt-10 font-serif text-4xl font-light leading-[1.08] tracking-tight md:mt-14 md:text-6xl">
                {t.consTitle}
              </h2>
            </FadeIn>
          </div>
          <FadeIn className="md:col-span-4 md:col-start-9" delay={0.15}>
            <p className="text-base leading-relaxed text-stone">{t.consP1}</p>
          </FadeIn>
        </div>
        <div className="mt-14 grid gap-12 md:mt-20 md:grid-cols-12 md:items-center">
          <FadeIn className="md:col-span-5" delay={0.1}>
            <ParallaxImage src={IMG.consulenza} alt={lang === "it" ? "Esterno di residenza contemporanea" : "Contemporary residence exterior"} className="aspect-[3/4] w-full" />
          </FadeIn>
          <FadeIn className="md:col-span-5 md:col-start-8" delay={0.2}>
            <p className="font-serif text-xl font-light italic leading-relaxed text-ink md:text-2xl">{t.consP2}</p>
            <div className="mt-10">
              <CTAButton to="/consulenza" variant="dark" testId="consulenza-cta">
                {t.consCta}
              </CTAButton>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FRAZIONAMENTI */}
      <section className="border-t border-ink/10">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
          <div className="grid gap-14 md:grid-cols-12">
            <div className="md:col-span-6">
              <FadeIn>
                <SectionLabel>{t.frazLabel}</SectionLabel>
                <h2 className="mt-10 font-serif text-4xl font-light leading-[1.08] tracking-tight md:mt-14 md:text-6xl">
                  {t.frazTitle}
                </h2>
                <p className="mt-9 max-w-lg text-base leading-relaxed text-stone">{t.frazP1}</p>
                <p className="mt-6 max-w-lg text-base leading-relaxed text-stone">{t.frazP2}</p>
              </FadeIn>
              <FadeIn delay={0.15}>
                <div className="mt-14 grid grid-cols-[auto_1fr_auto] items-center gap-6 md:gap-10">
                  <div className="w-32 md:w-44">
                    <div className="aspect-square p-2"><FloorPlanSingle /></div>
                    <div className="mt-3 text-center text-[10px] uppercase tracking-[0.22em] text-stone">{t.frazPlanA}</div>
                  </div>
                  <div className="flex items-center justify-center">
                    <span className="font-serif text-3xl font-light text-bronze">→</span>
                  </div>
                  <div className="w-32 md:w-44">
                    <div className="aspect-square p-2"><FloorPlanMulti /></div>
                    <div className="mt-3 text-center text-[10px] uppercase tracking-[0.22em] text-stone">{t.frazPlanB}</div>
                  </div>
                </div>
              </FadeIn>
            </div>
            <FadeIn className="md:col-span-4 md:col-start-9" delay={0.2}>
              <ol className="border-l border-ink/15">
                {t.frazSteps.map((s, i) => (
                  <li key={s} className="relative flex items-baseline gap-6 py-6 pl-8 md:py-7">
                    <span className={`absolute -left-[3px] top-1/2 h-1.5 w-1.5 -translate-y-1/2 rotate-45 ${i === 0 || i === t.frazSteps.length - 1 ? "bg-bronze" : "bg-ink/30"}`} />
                    <span className="font-serif text-lg font-light text-stone">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm uppercase tracking-[0.18em]">{s}</span>
                  </li>
                ))}
              </ol>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* CAMBI D'USO */}
      <section className="bg-ink text-ivory">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
          <div className="grid gap-14 md:grid-cols-12">
            <div className="md:col-span-5">
              <FadeIn>
                <SectionLabel dark>{t.usoLabel}</SectionLabel>
                <h2 className="mt-10 font-serif text-4xl font-light leading-[1.08] tracking-tight md:mt-14 md:text-6xl">
                  {t.usoTitle}
                </h2>
                <p className="mt-9 max-w-md text-base leading-relaxed text-ivory/70">{t.usoP1}</p>
                <p className="mt-10 max-w-md border-l border-bronze/60 pl-6 text-xs leading-relaxed text-ivory/50">
                  {t.usoNote}
                </p>
              </FadeIn>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              {t.usoRows.map(([from, to], i) => (
                <FadeIn key={from} delay={i * 0.08}>
                  <div className="group flex items-baseline justify-between gap-6 border-b border-ivory/15 py-7 transition-colors duration-500 hover:border-bronze/60 md:py-8">
                    <span className="font-serif text-2xl font-light text-ivory/60 transition-colors duration-500 group-hover:text-ivory md:text-4xl">
                      {from}
                    </span>
                    <span className="font-serif text-xl font-light text-bronze md:text-2xl">→</span>
                    <span className="text-right font-serif text-2xl font-light md:text-4xl">{to}</span>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* RISTRUTTURAZIONI */}
      <section className="border-t border-ink/10">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
          <div className="grid gap-12 md:grid-cols-12 md:items-center">
            <FadeIn className="md:col-span-6">
              <ParallaxImage
                src={IMG.ristrutturazioni}
                alt={lang === "it" ? "Interno ristrutturato — dettaglio contemporaneo" : "Renovated interior — contemporary detail"}
                className="aspect-[4/3] w-full"
              />
            </FadeIn>
            <FadeIn className="md:col-span-5 md:col-start-8" delay={0.12}>
              <SectionLabel>{t.ristLabel}</SectionLabel>
              <h2 className="mt-8 font-serif text-4xl font-light leading-[1.08] tracking-tight md:text-5xl">
                {t.ristTitle}
              </h2>
              <p className="mt-7 max-w-md text-base leading-relaxed text-stone">{t.ristText}</p>
              <div className="mt-9">
                <CTAButton to="/ristrutturazioni" variant="outline" testId="home-rist-cta">
                  {t.ristCta}
                </CTAButton>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* GENERAL CONTRACTOR */}
      <section className="bg-dove/40">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
          <div className="grid gap-12 md:grid-cols-12 md:items-center">
            <FadeIn className="md:col-span-5">
              <SectionLabel>{t.gcLabel}</SectionLabel>
              <h2 className="mt-8 font-serif text-4xl font-light leading-[1.08] tracking-tight md:text-5xl">
                {t.gcTitle}
              </h2>
              <p className="mt-7 max-w-md text-base leading-relaxed text-stone">{t.gcText}</p>
              <div className="mt-9">
                <CTAButton to="/general-contractor" variant="dark" testId="home-gc-cta">
                  {t.gcCta}
                </CTAButton>
              </div>
            </FadeIn>
            <FadeIn className="md:col-span-6 md:col-start-7" delay={0.12}>
              <ParallaxImage
                src={IMG.gc}
                alt={lang === "it" ? "Architettura residenziale contemporanea" : "Contemporary residential architecture"}
                className="aspect-[4/3] w-full"
              />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-7xl px-6 py-24 text-center md:px-10 md:py-40">
        <FadeIn>
          <h2 className="mx-auto max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight md:text-6xl">
            {t.finalTitle}
          </h2>
          <p className="mx-auto mt-7 max-w-md text-base leading-relaxed text-stone">{t.finalText}</p>
          <div className="mt-11 flex justify-center">
            <CTAButton to="/contatti" variant="dark" testId="final-cta">
              {lang === "it" ? "Proponici un immobile" : "Propose a property"}
            </CTAButton>
          </div>
        </FadeIn>
      </section>
    </div>
  );
}
